import { Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BugReport } from '../../../../../models/bug-reports/bug-report.model';
import { BugCategoryType } from '../../../../../enums/bug-reports/bug-category-type.enum';
import { UserReportsResultsResponse } from '../../../../../models/users/responses/api-users-reports-results-response.model';
import { ApiUsersService } from '../../../../../services/users/api/api-users.service';
import { ApiBugReportsService } from '../../../../../services/bug-reports/api/api-bug-reports.service';
import { AlertsService } from '../../../../../services/alerts.service';
import { DateService } from '../../../../../services/date.service';
import { BugReportDetailModalComponent } from '../../../../bug-reports/modals/bug-report-detail-modal/bug-report-detail-modal.component';
import { NewsUserAddModalComponent } from '../user-messages-tab/modals/news-user-add-modal/news-user-add-modal.component';
import { UserMatchDetailModalComponent } from '../user-matchs-tab/modals/user-match-detail-modal/user-match-detail-modal.component';

@Component({
  selector: 'app-user-reports-tab',
  templateUrl: './user-reports-tab.component.html',
  standalone: true,
  imports: [CommonModule, TooltipModule, BugReportDetailModalComponent, NewsUserAddModalComponent, UserMatchDetailModalComponent]
})
export class UserReportsTabComponent {

  @ViewChild('detailModal') detailModal!: BugReportDetailModalComponent;
  @ViewChild('newsUserAddModal') newsUserAddModal!: NewsUserAddModalComponent;
  @ViewChild('matchDetailModal') matchDetailModal!: UserMatchDetailModalComponent;

  @Input() userId: number = 0;
  @Input() userLocale: string = '';

  protected readonly BugCategoryType = BugCategoryType;

  isLoading: boolean = false;

  reports: BugReport[] = [];
  resultsPerPage: number = 25;
  pageNumber: number = 1;
  totalResults: number = 0;

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshReportsList();
  }

  constructor(public dateService: DateService, private apiUsersService: ApiUsersService, private apiBugReportsService: ApiBugReportsService, private alertsService: AlertsService) {}

  load(): void {
    this.pageNumber = 1;
    this.refreshReportsList();
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshReportsList();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshReportsList();
  }

  getBugTypeName(bugType: number): string {
    switch (bugType) {
      case BugCategoryType.CreditLost: return 'Credit Lost';
      case BugCategoryType.BuyIssue: return 'Buy Issue';
      case BugCategoryType.InGameIssue: return 'In-Game Issue';
      case BugCategoryType.GameIssue: return 'Game Issue';
      case BugCategoryType.Other: return 'Other';
      default: return 'None';
    }
  }

  onClickDetails(report: BugReport): void {
    this.detailModal.show(report);
  }

  onClickLastMatch(report: BugReport): void {
    if (!report.lastMatchId) return;
    this.matchDetailModal.show(report.userId, report.lastMatchId);
  }

  onClickSendMessage(report: BugReport): void {
    this.newsUserAddModal.show(report.userId, report.userLocale || this.userLocale, () => {
      this.apiBugReportsService.changeReportState(report.bugReportId, true).then(
        () => {
          this.alertsService.displayAlertSuccess('Message sent and report closed !');
          this.refreshReportsList();
        }, error => {
          this.alertsService.displayAlertError('Error closing report : ' + error);
        }
      );
    });
  }

  onClickToggleState(report: BugReport): void {
    const newState = !report.isClosed;
    this.apiBugReportsService.changeReportState(report.bugReportId, newState).then(
      () => {
        this.alertsService.displayAlertSuccess(newState ? 'Report closed !' : 'Report reopened !');
        this.refreshReportsList();
      }, error => {
        this.alertsService.displayAlertError('Error changing report state : ' + error);
      }
    );
  }

  private refreshReportsList(): void {
    this.isLoading = true;

    this.apiUsersService.getUserReports(this.userId, this.pageNumber, this.resultsPerPage).then(
      (response: UserReportsResultsResponse) => {
        this.isLoading = false;
        this.reports = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading reports (' + error + ')');
      }
    );
  }
}
